import type { TrainingSubmissionAttributes } from '@/server/models'

import {
  type SecondaryTrainingType,
  TutorTrainingSubmissionAssignmentPriority,
} from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import TrainingQ from './TrainingQ'
import UserQ from './UserQ'

const TrainingSubmissionQ = {
  ...makeQueryModelV2<TrainingSubmissionAttributes>('training_submission')([
    'id',
    'user_id',
    'training_id',
    'assignment_mentor_user_id',
    'assignment_priority',
    'assignment_note',
    'submitted_at',
    'submitted_score',
    'volunteer_activity_id',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Training: {
      query: TrainingQ,
      foreignKey: TrainingQ.id,
      sourceKey: TrainingSubmissionQ.training_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: TrainingSubmissionQ.user_id,
    },
  }),

  where: (
    {
      userId,
      trainingId,
      assignmentPriority,
      hasSubmitted,
      paranoid = true,
      include = [],
      limit = null,
      groupBy = null,
      order = null,
    }: {
      userId?: number | null | SQLFragment<number>
      trainingId?: number | null | SQLFragment<number>
      assignmentPriority?:
        | TutorTrainingSubmissionAssignmentPriority
        | SQLFragment<TutorTrainingSubmissionAssignmentPriority>
        | null
      hasSubmitted?: boolean | null | SQLFragment<boolean>
      paranoid?: boolean
      include?: IncludeAssociations
      limit?: number | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<TrainingSubmissionAttributes> => SQL`
        FROM ${TrainingSubmissionQ}
        ${includeAssociationsToSql(TrainingSubmissionQ, include)}
        WHERE
            ${userId != null ? SQL`${TrainingSubmissionQ.user_id} = ${userId} AND` : SQL``}
            ${
              trainingId != null
                ? SQL`${TrainingSubmissionQ.training_id} = ${trainingId} AND`
                : SQL``
            }
            ${
              assignmentPriority != null
                ? SQL`${TrainingSubmissionQ.assignment_priority} = ${assignmentPriority} AND`
                : SQL``
            }
            ${
              hasSubmitted != null
                ? SQL`${TrainingSubmissionQ.submitted_at} IS ${
                    hasSubmitted === false ? SQL`` : SQL`NOT`
                  } NULL AND`
                : SQL``
            }
            ${paranoid ? SQL`${TrainingSubmissionQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  hasUserOutstandingTutorTraining: ({
    userId,
  }: {
    userId: number | null | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`
        ((SELECT COUNT(*) ${TrainingSubmissionQ.where({
          userId,
          assignmentPriority: TutorTrainingSubmissionAssignmentPriority.REQUIRED,
          hasSubmitted: false,
        })}) > 0)
    `,

  hasUserCompletedTraining: ({
    secondary_type,
    userId,
  }: {
    secondary_type: SecondaryTrainingType | SQLFragment<SecondaryTrainingType>
    userId: number | null | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`
        ${TrainingSubmissionQ.submitted_at} IS NOT NULL as "status"
        FROM ${TrainingQ}
        LEFT JOIN ${TrainingSubmissionQ} ON (
            ${TrainingSubmissionQ.training_id} = ${TrainingQ.id}
            AND ${TrainingSubmissionQ.deleted_at} IS NULL
            AND ${TrainingSubmissionQ.user_id} = ${userId}
        )
        WHERE
            ${TrainingQ.deleted_at} IS NULL
            ${
              secondary_type != null
                ? SQL`AND ${TrainingQ.secondary_type} = ${secondary_type}`
                : SQL``
            }
        ORDER BY "status" ASC LIMIT 1
    `,

  countCompletions: ({
    trainingId,
    earliestDate,
    latestDate,
  }: {
    trainingId: number | SQLFragment<number>
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
  }): SQLFragment<number> =>
    SQL`COUNT(*)
        ${TrainingSubmissionQ.where(
          { trainingId },
          SQL`
            ${
              earliestDate != null
                ? SQL`${TrainingSubmissionQ.created_at} >= ${earliestDate}`
                : SQL`TRUE`
            } AND
            ${
              latestDate != null
                ? SQL`${TrainingSubmissionQ.created_at} <= ${latestDate}`
                : SQL`TRUE`
            }`
        )}`,
}

export default TrainingSubmissionQ
